import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Eye, X, Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PDFPrintDialog } from "@/components/PDFPrintDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { proveedoresAPI, chequesAPI, companyAPI, ProveedorPago, Proveedor, ProveedorRemito, Cheque, isForbiddenError, isServerError } from "@/lib/api";
import { openPdfTab } from "@/lib/openPdfTab";
import { parseMoney, formatMoneyAr, isFiniteMoney, formatDateOnlyEsAR, roundMoney } from "@/lib/utils";
import { roundToPeso } from "@/lib/roundToPeso";
import {
  formatArgentineMoneyPreview,
  formatArgentineMoneyTyping,
  toMoneyNumber,
} from "@/lib/parseArgentineDecimal";
import { dataRefresh } from "@/lib/dataRefresh";
import { DateInput } from "@/components/ui/date-input";
import {
  hoyCerradoUsaProximaCaja,
  isFechaPagoCajaPermitida,
  resolveFechaPagoDefault,
  toLocalYmd,
} from "./fechaPagoCaja";
import {
  arsDeLineaUsd,
  metodoPagoUsdPayload,
  normalizarTasa,
  usdAutoPorTasaRemito,
  usdSugeridoParaRestante,
} from "./pagoProveedorUsd";
import { NumberField } from "@/components/ui/number-field";

/** USD typing: dígitos + un `.` decimal (máx. 2), sin máscara AR. */
function formatUsdMoneyTyping(raw: string): string {
  let s = String(raw ?? "").replace(/[^\d.]/g, "");
  if (!s) return "";
  const firstDot = s.indexOf(".");
  if (firstDot >= 0) {
    const intDigits = s.slice(0, firstDot).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
    const frac = s.slice(firstDot + 1).replace(/\D/g, "").slice(0, 2);
    return `${intDigits}.${frac}`;
  }
  return s.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
}

function moneyOf(raw: string | number | null | undefined): number {
  return toMoneyNumber(raw) ?? 0;
}

function formatArsDisplay(value: number): string {
  if (!value || value <= 0) return "";
  return formatArgentineMoneyPreview(value);
}

function formatUsdDisplay(value: number): string {
  if (!value || value <= 0) return "";
  return roundMoney(value).toFixed(2);
}

function roundImporteOnBlur(raw: string, usd: boolean): string {
  return usd ? formatUsdDisplay(moneyOf(raw)) : formatArsDisplay(roundToPeso(moneyOf(raw)));
}

interface PaymentMethod {
  metodo: string;
  /** Display string (ARS masked o USD plain); parsear con moneyOf / toMoneyNumber. */
  importe: string;
  chequeId?: string;
  chequeTipo?: string;
  chequeNumero?: string;
  chequeBanco?: string;
  chequeFechaCreacion?: string;
  chequeFecha?: string;
  usarChequeExistente?: boolean;
  /** Solo Dólares: ARS por USD de esta línea, texto tal cual se tipea (como Ventas). */
  tasaCambio?: string;
}

export function ProveedoresPagos() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [refresh, setRefresh] = useState(0);
  const [pagos, setPagos] = useState<ProveedorPago[]>([]);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [remitos, setRemitos] = useState<ProveedorRemito[]>([]);
  const [availableCheques, setAvailableCheques] = useState<Cheque[]>([]);
  const [chequesCarteraStatus, setChequesCarteraStatus] = useState<
    "idle" | "ok" | "forbidden" | "error"
  >("idle");
  const [, setLoading] = useState(false);

  // Dialog state
  const [showNuevoPagoDialog, setShowNuevoPagoDialog] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState<Proveedor | null>(null);
  const [proveedorSearch, setProveedorSearch] = useState("");
  const [showProveedorSuggestions, setShowProveedorSuggestions] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([{ metodo: "", importe: "" }]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [selectedRemitoNumero, setSelectedRemitoNumero] = useState("");
  /** remito: imputa contra remito; adelanto: solo saldo proveedor (sin remito obligatorio) */
  const [pagoTipo, setPagoTipo] = useState<"remito" | "adelanto">("remito");
  const [isProcessing, setIsProcessing] = useState(false);
  /** F.3: librador en cheque propio (empresa); alineado con backend / CompanyConfig. */
  const [empresaChequeEmisor, setEmpresaChequeEmisor] = useState("Empresa");
  const [fechasPagoDisponibles, setFechasPagoDisponibles] = useState<string[]>([]);
  const [fechaPago, setFechaPago] = useState(toLocalYmd(new Date()));
  const [fechasPagoLoading, setFechasPagoLoading] = useState(false);
  const [showCajaSiguienteModal, setShowCajaSiguienteModal] = useState(false);
  const [showPDFDialog, setShowPDFDialog] = useState(false);
  const [currentPDFPago, setCurrentPDFPago] = useState<ProveedorPago | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [pagosData, proveedoresData, remitosData, empresaCfg] = await Promise.all([
          proveedoresAPI.getPagos(),
          proveedoresAPI.list(),
          proveedoresAPI.getRemitos(),
          companyAPI.getConfig().catch(() => null),
        ]);
        setPagos(pagosData);
        setProveedores(proveedoresData);
        setRemitos(remitosData);
        try {
          const chequesData = await chequesAPI.getDisponibles();
          setAvailableCheques(chequesData);
          setChequesCarteraStatus("ok");
        } catch (chequeErr) {
          if (isForbiddenError(chequeErr)) {
            console.error(
              "GET /api/cheques/disponibles/ 403 (cartera no incluida en pagos)",
              chequeErr,
            );
            setAvailableCheques([]);
            setChequesCarteraStatus("forbidden");
          } else {
            console.error("GET /api/cheques/disponibles/ failed", chequeErr);
            setChequesCarteraStatus("error");
            if (isServerError(chequeErr)) {
              toast.error("Error del servidor al cargar cheques disponibles.");
            } else {
              toast.error("No se pudieron cargar los cheques disponibles.");
            }
          }
        }
        const lib =
          empresaCfg?.razonSocial?.trim() ||
          empresaCfg?.nombreFantasia?.trim() ||
          "Empresa";
        setEmpresaChequeEmisor(lib);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error al cargar pagos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refresh]);

  const getProveedorInfo = (proveedorId: number) => {
    return proveedores.find(p => p.id === proveedorId) || { codigo: '?', nombreRazonSocial: 'Desconocido' };
  };

  const pedidoCell = (pago: ProveedorPago) => {
    const nro = (pago.pedidoNumero || pago.nroPedido || "").trim();
    if (nro) {
      return (
        <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">{nro}</span>
      );
    }
    if (pago.notas && /adelanto/i.test(pago.notas)) {
      return (
        <span className="px-2 py-1 rounded text-xs bg-amber-100 text-amber-900">Adelanto</span>
      );
    }
    if (pago.remitoNumero) {
      return (
        <span className="text-xs text-muted-foreground">Remito {pago.remitoNumero}</span>
      );
    }
    return <span className="text-muted-foreground">—</span>;
  };

  const filteredPagos = pagos.filter(p => {
    const prov = getProveedorInfo(p.proveedorId || 0);
    const pedidoStr = (p.pedidoNumero || p.nroPedido || "").toLowerCase();
    return !searchQuery ||
      prov.codigo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prov.nombreRazonSocial?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.pedidoId && p.pedidoId.toString().includes(searchQuery.toLowerCase())) ||
      pedidoStr.includes(searchQuery.toLowerCase()) ||
      (p.numeroPago && p.numeroPago.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.remitoNumero && p.remitoNumero.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const totalPages = Math.ceil(filteredPagos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPagos = filteredPagos.slice(startIndex, startIndex + itemsPerPage);

  // Proveedor search filter
  const filteredProveedores = proveedorSearch.trim()
    ? proveedores.filter(p =>
      p.nombreRazonSocial.toLowerCase().includes(proveedorSearch.toLowerCase()) ||
      p.codigo.toLowerCase().includes(proveedorSearch.toLowerCase())
    )
    : [];

  // Get remitos for selected proveedor
  const getProveedorRemitos = () => {
    if (!selectedProveedor) return [];
    // Use proveedorId comparison since proveedorCodigo may not always be present
    return remitos.filter(r =>
      r.proveedorCodigo === selectedProveedor.codigo ||
      r.proveedorId === selectedProveedor.id
    );
  };

  /** Remitos que aún admiten pago (excluye saldados / finalizados) */
  const getProveedorRemitosPagables = () => {
    return getProveedorRemitos().filter((r) => {
      const estado = (r.estado || "").toLowerCase();
      if (estado === "finalizado" || estado === "pagado") return false;
      const rest = roundToPeso(parseMoney(r.importe) - parseMoney(r.montoPagado));
      if (rest <= 0) return false;
      return true;
    });
  };

  const selectProveedor = (proveedor: Proveedor) => {
    setSelectedProveedor(proveedor);
    setProveedorSearch(proveedor.nombreRazonSocial);
    setShowProveedorSuggestions(false);
    setSelectedRemitoNumero("");
  };

  const addPaymentMethod = () => {
    const remaining = roundToPeso(getDeudaProveedor() - calculateTotalPago());
    setPaymentMethods([
      ...paymentMethods,
      { metodo: "", importe: remaining > 0 ? formatArsDisplay(remaining) : "" },
    ]);
  };

  const removePaymentMethod = (index: number) => {
    setPaymentMethods(paymentMethods.filter((_, i) => i !== index));
  };

  const updatePaymentMethod = (index: number, field: keyof PaymentMethod, value: string | number | boolean) => {
    const updated = [...paymentMethods];
    if (field === 'metodo' && value === 'Cheque') {
      updated[index] = {
        metodo: value as string,
        importe: "",
        chequeId: undefined,
        usarChequeExistente: chequesCarteraStatus === "ok",
        chequeTipo: undefined,
        chequeNumero: undefined,
        chequeBanco: undefined,
        chequeFechaCreacion: undefined,
        chequeFecha: undefined
      };
    } else if (field === 'chequeId') {
      const selectedCheque = availableCheques.find(c => c.codigo === value);
      updated[index] = {
        ...updated[index],
        chequeId: value as string,
        importe: formatArsDisplay(parseMoney(selectedCheque?.importe)),
      };
    } else if (field === 'usarChequeExistente' && value === false) {
      updated[index] = {
        ...updated[index],
        usarChequeExistente: false,
        chequeId: undefined,
        chequeFechaCreacion: new Date().toISOString().split('T')[0],
        chequeFecha: new Date().toISOString().split('T')[0],
        importe: (() => {
          const remaining = roundToPeso(getDeudaProveedor() - calculateTotalPago(index));
          return remaining > 0 ? formatArsDisplay(remaining) : "";
        })()
      };
    } else if (field === 'usarChequeExistente' && value === true) {
      updated[index] = {
        ...updated[index],
        usarChequeExistente: true,
        chequeTipo: undefined,
        chequeNumero: undefined,
        chequeBanco: undefined,
        chequeFechaCreacion: undefined,
        chequeFecha: undefined
      };
    } else if (field === 'metodo' && value === 'Dólares') {
      updated[index] = { metodo: value as string, importe: "", tasaCambio: undefined };
    } else if (field === 'tasaCambio') {
      const tasa = normalizarTasa(value);
      const current = updated[index];
      let importe = current.importe;
      const remaining = roundToPeso(getDeudaProveedor() - calculateTotalPago(index));
      const usdRemito = selectedRemitoNumero
        ? usdAutoPorTasaRemito(pagoTipo, remaining, tasa)
        : null;
      if (usdRemito !== null) {
        importe = formatUsdDisplay(usdRemito);
      } else if (pagoTipo === "adelanto" && !moneyOf(importe) && tasa > 0) {
        importe = formatUsdDisplay(usdSugeridoParaRestante(remaining, tasa));
      }
      updated[index] = { ...current, tasaCambio: String(value), importe };
    } else if (field === 'metodo') {
      // Cambio a método ARS: sugerir restante en máscara AR
      const remaining = roundToPeso(getDeudaProveedor() - calculateTotalPago(index));
      updated[index] = {
        metodo: value as string,
        importe: remaining > 0 ? formatArsDisplay(remaining) : "",
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setPaymentMethods(updated);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setPdfUrl(URL.createObjectURL(file));
      toast.success(`PDF "${file.name}" cargado`);
    } else {
      toast.error("Por favor seleccione un archivo PDF válido");
    }
  };

  const calculateTotalPago = (excludeIndex?: number) => {
    return roundToPeso(
      paymentMethods.reduce((sum, pm, i) => {
        if (excludeIndex !== undefined && i === excludeIndex) return sum;
        const importe = moneyOf(pm.importe);
        if (pm.metodo === 'Dólares') return sum + arsDeLineaUsd(importe, pm.tasaCambio);
        return sum + importe;
      }, 0)
    );
  };

  const getDeudaProveedor = () => {
    if (!selectedProveedor) return 0;
    if (pagoTipo === "adelanto") {
      // E.2: saldo negativo = deuda con el proveedor (ARS a cubrir)
      const s = parseMoney(selectedProveedor.saldo);
      return s < 0 ? roundToPeso(Math.abs(s)) : 0;
    }
    if (!selectedRemitoNumero) return 0;
    const remito = remitos.find(r => (r.nroRemito || r.numeroRemito) === selectedRemitoNumero);
    if (!remito) return 0;
    return roundToPeso(parseMoney(remito.importe) - parseMoney(remito.montoPagado));
  };

  const calculateSaldo = () => {
    return roundToPeso(calculateTotalPago() - getDeudaProveedor());
  };

  useEffect(() => {
    if (!showNuevoPagoDialog) return;
    let cancelled = false;
    setFechasPagoLoading(true);
    proveedoresAPI
      .fechasPagoDisponibles()
      .then((data) => {
        if (cancelled) return;
        const fechas = Array.isArray(data?.fechas) ? data.fechas : [];
        setFechasPagoDisponibles(fechas);
        const hoy = toLocalYmd(new Date());
        setFechaPago(resolveFechaPagoDefault(fechas, data?.default, hoy));
      })
      .catch(() => {
        if (cancelled) return;
        setFechasPagoDisponibles([]);
      })
      .finally(() => {
        if (!cancelled) setFechasPagoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showNuevoPagoDialog]);

  const debeAvisarCajaSiguiente = () =>
    hoyCerradoUsaProximaCaja(fechasPagoDisponibles, fechaPago, toLocalYmd(new Date()));

  const handleGuardarPago = async (opts?: { skipCajaWarning?: boolean }) => {
    if (!selectedProveedor) {
      toast.error("Debe seleccionar un proveedor");
      return;
    }
    if (!fechaPago || fechasPagoDisponibles.length === 0) {
      toast.error("No hay una fecha de caja disponible para el pago");
      return;
    }
    if (!isFechaPagoCajaPermitida(fechaPago, fechasPagoDisponibles)) {
      toast.error("Esa fecha no tiene caja disponible para cerrar");
      return;
    }
    if (pagoTipo === "remito" && !selectedRemitoNumero) {
      toast.error("Debe seleccionar un remito");
      return;
    }
    if (!paymentMethods.some(pm => pm.metodo && moneyOf(pm.importe) > 0)) {
      toast.error("Debe agregar al menos un método de pago válido");
      return;
    }

    if (
      paymentMethods.some(
        pm => pm.metodo === "Dólares" && moneyOf(pm.importe) > 0 && normalizarTasa(pm.tasaCambio) <= 0,
      )
    ) {
      toast.error("Ingrese la tasa de cambio del pago en dólares");
      return;
    }

    const totalPagoValidacion = calculateTotalPago();
    if (!isFiniteMoney(totalPagoValidacion) || totalPagoValidacion <= 0) {
      toast.error("El total del pago no es válido");
      return;
    }

    const chequeNuevos = paymentMethods.filter(pm => pm.metodo === "Cheque" && pm.usarChequeExistente === false);
    for (const cheque of chequeNuevos) {
      if (!cheque.chequeTipo || !cheque.chequeNumero || !cheque.chequeBanco || !cheque.chequeFechaCreacion || !cheque.chequeFecha) {
        toast.error("Complete todos los datos de los cheques nuevos");
        return;
      }
      if (cheque.chequeNumero.length > 11) {
        toast.error("El número de cheque no puede tener más de 11 dígitos");
        return;
      }
      if (moneyOf(cheque.importe) <= 0) {
        toast.error("Los cheques deben tener un importe mayor a 0");
        return;
      }
    }

    const chequeExistentes = paymentMethods.filter(
      pm => pm.metodo === "Cheque" && pm.usarChequeExistente !== false
    );
    for (const ch of chequeExistentes) {
      if (!ch.chequeId?.trim()) {
        toast.error("Seleccione un cheque existente");
        return;
      }
    }

    if (!opts?.skipCajaWarning && debeAvisarCajaSiguiente()) {
      setShowCajaSiguienteModal(true);
      return;
    }

    setIsProcessing(true);

    try {
      const eligible = paymentMethods.filter(pm => pm.metodo && moneyOf(pm.importe) > 0);
      let usoChequeEnPago = false;
      const metodosPago: Array<{
        tipo: string;
        monto: number;
        montoUsd?: number;
        cotizacion?: number;
        chequeId?: string;
        chequeNumero?: string;
        chequeBanco?: string;
        chequeTipo?: string;
        chequeFechaCreacion?: string;
        chequeFecha?: string;
      }> = [];

      for (const pm of eligible) {
        const tipo = pm.metodo.toLowerCase().replace(/\s+/g, "_");
        if (pm.metodo === "Dólares") {
          metodosPago.push({ tipo, ...metodoPagoUsdPayload(moneyOf(pm.importe), pm.tasaCambio) });
          continue;
        }
        const monto = roundToPeso(moneyOf(pm.importe));

        if (pm.metodo === "Cheque" && pm.usarChequeExistente === false) {
          if (selectedProveedor?.id == null) {
            toast.error("Proveedor sin identificador interno; recargue la lista de proveedores.");
            setIsProcessing(false);
            return;
          }
          usoChequeEnPago = true;
          const { codigo } = await chequesAPI.getNextCodigo();
          await chequesAPI.create({
            codigo,
            numeroCheque: pm.chequeNumero || "",
            banco: pm.chequeBanco || "",
            fechaEmision: pm.chequeFechaCreacion!,
            fechaCobro: pm.chequeFecha || pm.chequeFechaCreacion!,
            importe: monto,
            emisor: empresaChequeEmisor,
            origen: "proveedor",
            origenId: selectedProveedor.id,
            tipo: "propio",
            tipoCheque: pm.chequeTipo === "electronico" ? "electronico" : "fisico",
            moneda: "ARS",
            cobrado: false,
          });
          metodosPago.push({
            tipo,
            monto,
            chequeId: codigo,
            chequeNumero: pm.chequeNumero,
            chequeBanco: pm.chequeBanco,
            chequeTipo: pm.chequeTipo,
            chequeFechaCreacion: pm.chequeFechaCreacion,
            chequeFecha: pm.chequeFecha,
          });
        } else {
          if (pm.metodo === "Cheque") {
            usoChequeEnPago = true;
          }
          metodosPago.push({
            tipo,
            monto,
            chequeId: pm.chequeId,
            chequeNumero: pm.chequeNumero,
            chequeBanco: pm.chequeBanco,
            chequeTipo: pm.chequeTipo,
            chequeFechaCreacion: pm.chequeFechaCreacion,
            chequeFecha: pm.chequeFecha,
          });
        }
      }

      const totalPago = roundToPeso(calculateTotalPago());
      const saldo = calculateSaldo();

      // E.2: saldo cuenta y deuda snapshot los calcula el backend; no enviar saldo/deuda del front (evita 400 por Decimal).
      const created = await proveedoresAPI.createPago(selectedProveedor.codigo, {
        numeroPago: `PAG-${Date.now()}`,
        remitoNumeroLookup: pagoTipo === "remito" ? selectedRemitoNumero : undefined,
        notas: pagoTipo === "adelanto" ? "Adelanto a proveedor (sin remito)" : undefined,
        fechaPago,
        totalPago,
        metodosPago,
        proveedorCodigo: selectedProveedor.codigo
      });

      let message = "Pago registrado exitosamente";
      if (saldo > 0) message += ` - Sobrepago: $${formatMoneyAr(saldo)} (crédito a favor)`;
      else if (saldo < 0) message += ` - Deuda restante: $${formatMoneyAr(Math.abs(saldo))}`;
      else message += ` - Pago exacto`;

      toast.success(message);
      dataRefresh.emit("proveedores");
      if (usoChequeEnPago) {
        dataRefresh.emit("cheques");
      }

      // Reset form
      setSelectedProveedor(null);
      setProveedorSearch("");
      setPaymentMethods([{ metodo: "", importe: "" }]);
      setPdfFile(null);
      setPdfUrl("");
      setSelectedRemitoNumero("");
      setPagoTipo("remito");
      setShowCajaSiguienteModal(false);
      setShowNuevoPagoDialog(false);
      setRefresh(prev => prev + 1);
      if (created?.id) {
        setTimeout(() => {
          setCurrentPDFPago(created);
          setShowPDFDialog(true);
        }, 500);
      }
    } catch (error: any) {
      console.error("Error creating pago:", error);
      const code = String(error?.error || error?.data?.error || "");
      if (code === "dia_cerrado" || code === "caja_no_disponible") {
        toast.error(
          error?.message ||
            error?.data?.detail ||
            "No se puede cargar un pago: esa caja no está disponible",
        );
      } else {
        toast.error(error?.message || "Error al guardar el pago");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const getChequeDisplay = (chequeId: string) => {
    const cheque = availableCheques.find(c => c.codigo === chequeId);
    if (!cheque) return chequeId;
    return `${cheque.numeroCheque} - ${cheque.banco} - $${formatMoneyAr(cheque.importe)}`;
  };

  const generateAndDownloadPDF = (pago: ProveedorPago) => {
    setCurrentPDFPago(pago);
    setShowPDFDialog(true);
  };

  const handlePDFDownload = async () => {
    if (!currentPDFPago?.id) return;
    try {
      setDownloadingPDF(currentPDFPago.id);
      const file = await proveedoresAPI.downloadPagoPdf(currentPDFPago.id);
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo generar el PDF del recibo");
    } finally {
      setDownloadingPDF(null);
    }
  };

  const handlePDFPrint = async () => {
    if (!currentPDFPago?.id) return;
    try {
      setDownloadingPDF(currentPDFPago.id);
      const file = await proveedoresAPI.downloadPagoPdf(currentPDFPago.id);
      const printWindow = await openPdfTab(file, undefined, {
        path: `/api/proveedores-pagos/${currentPDFPago.id}/pdf/`,
      });
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        toast.error("No se pudo abrir la ventana de impresión");
      }
    } catch {
      toast.error("No se pudo generar el PDF del recibo");
    } finally {
      setDownloadingPDF(null);
    }
  };

  return (
    <>
      <div className="space-y-4 mt-4">
        <div className="flex gap-2 items-end justify-between">
          <div className="max-w-md flex-1">
            <label className="block text-sm font-medium mb-1">Buscar por código, nombre o pedido...</label>
            <input
              type="text"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            />
          </div>
          <Button
            size="sm"
            onClick={() => setShowNuevoPagoDialog(true)}
            className="bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
          >
            <Plus className="h-4 w-4 mr-1" />
            Nuevo Pago
          </Button>
        </div>

        {!searchQuery && paginatedPagos.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No hay datos para mostrar
          </div>
        ) : filteredPagos.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No se encontraron resultados
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-2 text-left">Código</th>
                    <th className="px-4 py-2 text-left">Proveedor</th>
                    <th className="px-4 py-2 text-left">Nro de Pedido</th>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-left">Total Pago</th>
                    <th className="px-4 py-2 text-left">Deuda</th>
                    <th className="px-4 py-2 text-left">Saldo</th>
                    <th className="px-4 py-2 text-left">Estado</th>
                    <th className="px-4 py-2 text-left">Recibo PDF</th>
                    <th className="px-4 py-2 text-left">Factura PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPagos.map((pago, idx) => (
                    <tr key={pago.id ?? pago.numeroPago ?? idx} className="border-b hover:bg-muted/50">
                      <td className="px-4 py-2 font-mono text-xs">{getProveedorInfo(pago.proveedorId || 0).codigo}</td>
                      <td className="px-4 py-2">{getProveedorInfo(pago.proveedorId || 0).nombreRazonSocial}</td>
                      <td className="px-4 py-2">{pedidoCell(pago)}</td>
                      <td className="px-4 py-2">{formatDateOnlyEsAR(pago.fechaPago || pago.fecha) || "—"}</td>
                      <td className="px-4 py-2">
                        <span className="font-medium">
                          ${(pago.totalPago || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-2">${(pago.deudaProveedor || 0).toLocaleString()}</td>
                      <td className="px-4 py-2">
                        <span className={`font-medium ${(pago.saldo || 0) > 0 ? 'text-green-600' : (pago.saldo || 0) < 0 ? 'text-red-600' : ''}`}>
                          ${Math.abs(pago.saldo || 0).toLocaleString()}
                          {(pago.saldo || 0) > 0 && ' (+)'}
                          {(pago.saldo || 0) < 0 && ' (-)'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs ${(pago.totalPago || 0) > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {(pago.totalPago || 0) > 0 ? 'Pagado' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {pago.id ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            type="button"
                            onClick={() => generateAndDownloadPDF(pago)}
                            disabled={downloadingPDF === pago.id}
                            className="h-8 w-8"
                            title="Descargar Recibo PDF"
                            aria-label="Descargar Recibo PDF"
                          >
                            {downloadingPDF === pago.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {pago.facturaPdfName ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(pago.facturaPdfUrl, '_blank')}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {pago.facturaPdfName}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4">
                <div className="text-sm text-muted-foreground">
                  Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, filteredPagos.length)} de {filteredPagos.length} registros
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Atrás
                  </Button>
                  <div className="flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <Button
                        key={page}
                        variant={page === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className="w-8"
                      >
                        {page}
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Nuevo Pago Dialog */}
      <Dialog
        open={showNuevoPagoDialog}
        onOpenChange={(open) => {
          setShowNuevoPagoDialog(open);
          if (!open) {
            setPagoTipo("remito");
            setSelectedRemitoNumero("");
            setShowCajaSiguienteModal(false);
          }
        }}
      >
        <DialogContent className="max-w-3xl sm:max-w-[532px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Pago a Proveedor</DialogTitle>
            <DialogDescription>
              Complete la información del pago
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Proveedor Selection */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Buscar Proveedor</label>
              <input
                type="text"
                value={proveedorSearch}
                onChange={(e) => {
                  setProveedorSearch(e.target.value);
                  setShowProveedorSuggestions(true);
                }}
                onFocus={() => setShowProveedorSuggestions(true)}
                placeholder="Buscar proveedor por nombre o código..."
                disabled={isProcessing}
                className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
              {showProveedorSuggestions && filteredProveedores.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-background border border-input rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredProveedores.map((proveedor) => (
                    <button
                      key={proveedor.codigo}
                      type="button"
                      onClick={() => selectProveedor(proveedor)}
                      className="w-full px-4 py-2 text-left hover:bg-muted text-sm flex justify-between items-center"
                    >
                      <span>{proveedor.nombreRazonSocial}</span>
                      <span className="text-xs text-muted-foreground">{proveedor.codigo}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="fechaPagoProveedor">
                Fecha de pago (caja disponible)
              </label>
              {fechasPagoLoading ? (
                <p className="text-sm text-muted-foreground">Cargando fechas…</p>
              ) : fechasPagoDisponibles.length === 0 ? (
                <p className="text-sm text-destructive">
                  No hay caja disponible para cerrar. No se puede cargar el pago.
                </p>
              ) : (
                <>
                  <DateInput
                    id="fechaPagoProveedor"
                    value={fechaPago}
                    onChange={(ymd) => {
                      if (!ymd) return;
                      if (!isFechaPagoCajaPermitida(ymd, fechasPagoDisponibles)) {
                        toast.error("Esa fecha no tiene caja disponible para cerrar");
                        return;
                      }
                      setFechaPago(ymd);
                    }}
                    onDisallowedDate={() => {
                      toast.error("Esa fecha no tiene caja disponible para cerrar");
                    }}
                    allowedDates={fechasPagoDisponibles}
                    popoverModal
                    disabled={isProcessing}
                    placeholder="dd/mm/yyyy"
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Calendario de la app. Solo fechas con cierre diario abierto de Casa central;
                    un día cerrado no se puede elegir.
                  </p>
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tipo de pago</label>
              <div className="flex flex-wrap gap-6 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="pagoTipoProveedor"
                    checked={pagoTipo === "remito"}
                    onChange={() => {
                      setPagoTipo("remito");
                    }}
                    disabled={isProcessing}
                    className="h-4 w-4"
                  />
                  <span>Remito</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="pagoTipoProveedor"
                    checked={pagoTipo === "adelanto"}
                    onChange={() => {
                      setPagoTipo("adelanto");
                      setSelectedRemitoNumero("");
                    }}
                    disabled={isProcessing}
                    className="h-4 w-4"
                  />
                  <span>Adelanto</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Remito: imputa contra un remito con saldo. Adelanto: reduce el saldo del proveedor sin remito.
              </p>
            </div>

            {/* Remito Selection */}
            {pagoTipo === "remito" && (
            <div>
              <label className="block text-sm font-medium mb-1">Remito relacionado</label>
              <select
                value={selectedRemitoNumero}
                onChange={(e) => setSelectedRemitoNumero(e.target.value)}
                disabled={isProcessing || !selectedProveedor}
                className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              >
                <option value="">Seleccionar remito...</option>
                {selectedProveedor && getProveedorRemitosPagables().map((remito) => {
                  const remitoNum = remito.nroRemito || remito.numeroRemito || remito.codigo;
                  const montoRestante = roundToPeso(parseMoney(remito.importe) - parseMoney(remito.montoPagado));
                  return (
                    <option key={remitoNum} value={remitoNum}>
                      Remito: {remitoNum} - Total: ${formatMoneyAr(remito.importe)} - Pagado: ${formatMoneyAr(remito.montoPagado)} - Resta: ${formatMoneyAr(montoRestante)} - {remito.estado}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Solo se listan remitos con saldo pendiente (no finalizados ni totalmente pagados).
              </p>
            </div>
            )}

            {/* Payment Methods */}
            <div className="space-y-3">
              <label className="block text-sm font-medium">Métodos de Pago</label>
              {paymentMethods.map((pm, index) => (
                <div key={index} className="border border-input rounded-lg p-3 space-y-3">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Método</label>
                      <select
                        value={pm.metodo}
                        onChange={(e) => updatePaymentMethod(index, 'metodo', e.target.value)}
                        disabled={isProcessing}
                        className="w-full px-3 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Efectivo">Efectivo</option>
                        <option value="Transferencia">Transferencia</option>
                        <option value="Tarjeta de Crédito">Tarjeta de Crédito</option>
                        <option value="Tarjeta de Débito">Tarjeta de Débito</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Dólares">Dólares</option>
                      </select>
                    </div>
                    {pm.metodo !== 'Cheque' && (
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-1">
                          {pm.metodo === 'Dólares' ? 'Importe (USD)' : 'Importe (ARS)'}
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={pm.importe}
                          onChange={(e) =>
                            updatePaymentMethod(
                              index,
                              'importe',
                              pm.metodo === 'Dólares'
                                ? formatUsdMoneyTyping(e.target.value)
                                : formatArgentineMoneyTyping(e.target.value),
                            )
                          }
                          onBlur={() =>
                            updatePaymentMethod(
                              index,
                              'importe',
                              roundImporteOnBlur(pm.importe, pm.metodo === 'Dólares'),
                            )
                          }
                          placeholder={pm.metodo === 'Dólares' ? '0.00' : '0,00'}
                          disabled={isProcessing}
                          className="w-full px-3 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                        />
                        {pm.metodo === 'Dólares' && moneyOf(pm.importe) > 0 && normalizarTasa(pm.tasaCambio) > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            = ${formatMoneyAr(arsDeLineaUsd(moneyOf(pm.importe), pm.tasaCambio))} ARS
                          </div>
                        )}
                      </div>
                    )}
                    {pm.metodo === 'Dólares' && (
                      <div className="flex-1">
                        <label htmlFor={`pago-prov-tasa-${index}`} className="block text-sm font-medium mb-1">
                          Tasa de Cambio (USD/ARS)
                        </label>
                        <NumberField
                          id={`pago-prov-tasa-${index}`}
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          value={pm.tasaCambio ?? ''}
                          onChange={(e) => updatePaymentMethod(index, 'tasaCambio', e.target.value)}
                          placeholder="0.00"
                          disabled={isProcessing}
                          className="w-full px-3 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                        />
                      </div>
                    )}
                    {paymentMethods.length > 1 && (
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => removePaymentMethod(index)}
                        disabled={isProcessing}
                        className="h-10 w-10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Cheque section */}
                  {pm.metodo === 'Cheque' && (
                    <div className="space-y-2 pt-2 border-t border-input">
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`cheque-type-${index}`}
                            checked={pm.usarChequeExistente !== false}
                            onChange={() => updatePaymentMethod(index, 'usarChequeExistente', true)}
                            disabled={isProcessing || chequesCarteraStatus !== "ok"}
                            className="w-4 h-4"
                          />
                          <span className="text-xs">Usar existente</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`cheque-type-${index}`}
                            checked={pm.usarChequeExistente === false}
                            onChange={() => updatePaymentMethod(index, 'usarChequeExistente', false)}
                            disabled={isProcessing}
                            className="w-4 h-4"
                          />
                          <span className="text-xs">Crear nuevo</span>
                        </label>
                      </div>
                      {chequesCarteraStatus === "forbidden" && (
                        <p className="text-xs text-muted-foreground">
                          La cartera de cheques no está incluida en este trabajo. Use crear nuevo.
                        </p>
                      )}
                      {chequesCarteraStatus === "error" && (
                        <p className="text-xs text-destructive">
                          No se pudieron cargar los cheques disponibles (error de servidor).
                        </p>
                      )}

                      {pm.usarChequeExistente !== false ? (
                        <div>
                          <label className="block text-xs font-medium mb-1">Seleccionar Cheque</label>
                          {chequesCarteraStatus === "ok" ? (
                          <select
                            value={pm.chequeId || ""}
                            onChange={(e) => updatePaymentMethod(index, 'chequeId', e.target.value)}
                            disabled={isProcessing}
                            className="w-full px-3 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                          >
                            <option value="">
                              {availableCheques.length === 0
                                ? "No hay cheques disponibles"
                                : "Seleccionar cheque..."}
                            </option>
                            {availableCheques.map((cheque) => (
                              <option key={cheque.codigo} value={cheque.codigo}>
                                {getChequeDisplay(cheque.codigo)}
                              </option>
                            ))}
                          </select>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              {chequesCarteraStatus === "error"
                                ? "El combo no está disponible por un error de servidor."
                                : "El combo de cartera no está incluido en este trabajo."}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium mb-1">Tipo</label>
                            <select
                              value={pm.chequeTipo || ""}
                              onChange={(e) => updatePaymentMethod(index, 'chequeTipo', e.target.value)}
                              disabled={isProcessing}
                              className="w-full px-3 py-2 border border-input bg-input rounded-lg text-sm"
                            >
                              <option value="">Seleccionar...</option>
                              <option value="fisico">Físico</option>
                              <option value="electronico">Electrónico</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Número</label>
                            <input
                              type="text"
                              value={pm.chequeNumero || ""}
                              onChange={(e) => updatePaymentMethod(index, 'chequeNumero', e.target.value)}
                              placeholder="Número de cheque"
                              maxLength={11}
                              disabled={isProcessing}
                              className="w-full px-3 py-2 border border-input bg-input rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Banco</label>
                            <input
                              type="text"
                              value={pm.chequeBanco || ""}
                              onChange={(e) => updatePaymentMethod(index, 'chequeBanco', e.target.value)}
                              placeholder="Nombre del banco"
                              disabled={isProcessing}
                              className="w-full px-3 py-2 border border-input bg-input rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Importe (ARS)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={pm.importe}
                              onChange={(e) =>
                                updatePaymentMethod(
                                  index,
                                  'importe',
                                  formatArgentineMoneyTyping(e.target.value),
                                )
                              }
                              onBlur={() =>
                                updatePaymentMethod(
                                  index,
                                  'importe',
                                  roundImporteOnBlur(pm.importe, false),
                                )
                              }
                              placeholder="0,00"
                              disabled={isProcessing}
                              className="w-full px-3 py-2 border border-input bg-input rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Fecha Emisión</label>
                            <DateInput
                              value={pm.chequeFechaCreacion || ""}
                              onChange={(ymd) => updatePaymentMethod(index, 'chequeFechaCreacion', ymd)}
                              disabled={isProcessing}
                              popoverModal
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Fecha Cobro</label>
                            <DateInput
                              value={pm.chequeFecha || ""}
                              onChange={(ymd) => updatePaymentMethod(index, 'chequeFecha', ymd)}
                              disabled={isProcessing}
                              popoverModal
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <Button
                variant="outline"
                size="sm"
                onClick={addPaymentMethod}
                disabled={isProcessing}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-1" /> Agregar Método de Pago
              </Button>
            </div>

            {/* PDF Upload */}
            <div>
              <label className="block text-sm font-medium mb-1">Factura PDF (Opcional)</label>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileUpload}
                  disabled={isProcessing}
                  className="hidden"
                  id="pdf-upload"
                />
                <label
                  htmlFor="pdf-upload"
                  className="flex-1 px-4 py-2 border border-dashed border-input rounded-lg cursor-pointer hover:bg-muted/50 flex items-center justify-center gap-2 text-sm"
                >
                  <Upload className="h-4 w-4" />
                  {pdfFile ? pdfFile.name : "Seleccionar archivo PDF"}
                </label>
                {pdfFile && (
                  <Button variant="outline" size="sm" onClick={() => window.open(pdfUrl, '_blank')}>
                    <Eye className="h-4 w-4 mr-1" /> Ver
                  </Button>
                )}
              </div>
            </div>

            {/* Totals Summary */}
            {selectedRemitoNumero && (
              <div className="border border-input rounded-lg p-4 bg-muted/30 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Deuda Remito:</span>
                  <span className="font-medium">${formatMoneyAr(getDeudaProveedor())}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Total Pago:</span>
                  <span className="font-medium text-green-600">${formatMoneyAr(calculateTotalPago())}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium">Saldo:</span>
                  <span className={`font-bold ${calculateSaldo() > 0 ? 'text-green-600' : calculateSaldo() < 0 ? 'text-red-600' : ''}`}>
                    ${formatMoneyAr(Math.abs(calculateSaldo()))}
                    {calculateSaldo() > 0 && ' (A favor)'}
                    {calculateSaldo() < 0 && ' (Adeudado)'}
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowNuevoPagoDialog(false)}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => void handleGuardarPago()}
                disabled={isProcessing || fechasPagoDisponibles.length === 0 || !fechaPago}
                className="bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
              >
                {isProcessing ? "Guardando..." : "Guardar Pago"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showCajaSiguienteModal} onOpenChange={setShowCajaSiguienteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cierre diario cerrado</AlertDialogTitle>
            <AlertDialogDescription>
              El cierre diario de hoy ya está cerrado. El pago se cargará con la
              fecha del día siguiente (
              {formatDateOnlyEsAR(fechaPago) || fechaPago}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleGuardarPago({ skipCajaWarning: true });
              }}
            >
              Confirmar y guardar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PDFPrintDialog
        open={showPDFDialog}
        onOpenChange={setShowPDFDialog}
        onDownload={handlePDFDownload}
        onPrint={handlePDFPrint}
        title="PDF del recibo"
        description="¿Desea descargar o imprimir el recibo del pago?"
      />
    </>
  );
}